<template>
  <article class="gs-promo-card">
    <header class="gs-promo-card__head">
      <div class="gs-promo-card__identity">
        <h3 class="gs-promo-card__name">{{ resource.name }}</h3>
        <gs-badge v-if="resource.is_default" variant="success">Default</gs-badge>
      </div>
      <div class="gs-promo-card__status">
        <span class="gs-promo-card__status-label">{{ resource.is_active ? 'Live' : 'Paused' }}</span>
        <gd-toggle
          :value="!!resource.is_active"
          :aria-label="resource.is_active ? 'Pause this portal' : 'Make this portal live'"
          @change="$emit('toggle-status', $event)"
        />
      </div>
    </header>

    <dl class="gs-promo-card__facts">
      <div class="gs-promo-card__fact">
        <dt>{{ $t('frontend_product_step9_landing_page') }}</dt>
        <dd>{{ facts.landingPageName || 'Not set' }}</dd>
      </div>
      <div class="gs-promo-card__fact">
        <dt>{{ $t('frontend_date') }}</dt>
        <dd>{{ facts.createdAt || '—' }}</dd>
      </div>
      <div class="gs-promo-card__fact">
        <dt>Assets</dt>
        <dd>{{ facts.filledTypes }} of {{ facts.totalTypes }} filled</dd>
      </div>
    </dl>

    <div class="gs-promo-card__actions">
      <gs-button
        variant="outline"
        size="sm"
        data-action="edit-assets"
        :title="$t('frontend_groovesell_tools_affiliate_tooltip_edit')"
        @click="$emit('edit-assets')"
      >
        <gs-icon name="edit" size="16" />
        Edit assets
      </gs-button>

      <gs-button
        variant="outline"
        size="sm"
        data-action="share"
        :disabled="!shareEnabled"
        :title="shareTitle"
        @click="$emit('share')"
      >
        <gs-icon name="share" size="16" />
        Share &amp; embed
      </gs-button>

      <span class="gs-promo-card__actions-spacer" aria-hidden="true"></span>

      <gs-button
        variant="ghost"
        size="icon"
        data-action="copy-signup-link"
        :disabled="!shareEnabled"
        :aria-label="$t('frontend_groovesell_tools_affiliate_tooltip_affiliate_login')"
        :title="copySignupLinkTitle"
        @click="$emit('copy-signup-link')"
      >
        <gs-icon name="link" size="16" />
      </gs-button>

      <gs-button
        variant="ghost"
        size="icon"
        data-action="settings"
        :aria-label="$t('frontend_groovesell_tools_affiliate_tooltip_settings')"
        :title="$t('frontend_groovesell_tools_affiliate_tooltip_settings')"
        @click="$emit('settings')"
      >
        <gs-icon name="settings" size="16" />
      </gs-button>

      <gs-button
        v-if="!resource.is_default"
        variant="ghost"
        size="icon"
        data-action="make-default"
        aria-label="Make this the default portal"
        title="Make this the default portal"
        @click="$emit('make-default')"
      >
        <gs-icon name="star" size="16" />
      </gs-button>

      <gs-button
        variant="ghost"
        size="icon"
        data-action="preview"
        aria-label="Preview the affiliate portal"
        title="Preview the affiliate portal"
        @click="$emit('preview')"
      >
        <gs-icon name="eye" size="16" />
      </gs-button>

      <gs-button
        variant="ghost"
        size="icon"
        class="is-destructive"
        data-action="delete"
        :disabled="!!resource.is_default"
        :aria-label="$t('frontend_groovesell_tools_affiliate_tooltip_delete')"
        :title="deleteTitle"
        @click="requestDelete()"
      >
        <gs-icon name="trash" size="16" />
      </gs-button>
    </div>
  </article>
</template>

<script>
import { promoAreaFacts } from '../promoAreaFacts';

export default {
  name: 'PromoAreaCard',
  props: {
    resource: {
      type: Object,
      default: () => ({}),
    },
    funnel: {
      type: Object,
      default: () => ({}),
    },
    shareEnabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    facts() {
      return promoAreaFacts(this.resource, this.funnel);
    },
    // The enabled tooltips are the ones the legacy action row carried; the
    // disabled ones say why the action is unavailable, which the old row never
    // did and which has no key of its own yet.
    shareTitle() {
      return this.shareEnabled
        ? this.$t('frontend_groovesell_tools_affiliate_tooltip_promotion_tools')
        : 'Publish this funnel to share or embed the portal';
    },
    copySignupLinkTitle() {
      return this.shareEnabled
        ? this.$t('frontend_groovesell_tools_affiliate_tooltip_affiliate_login')
        : 'Publish this funnel to copy the affiliate signup link';
    },
    deleteTitle() {
      return this.resource.is_default
        ? "The default promo tools can't be deleted"
        : this.$t('frontend_groovesell_tools_affiliate_tooltip_delete');
    },
  },
  methods: {
    requestDelete() {
      if (this.resource.is_default) {
        return;
      }

      this.$emit('delete');
    },
  },
};
</script>

<style scoped>
.gs-promo-card {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px 24px;
  color: var(--gs-foreground);
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-sm);
  transition: box-shadow .15s ease, transform .15s ease;
}

.gs-promo-card:hover {
  box-shadow: var(--gs-shadow-md);
  transform: translateY(-1px);
}

.gs-promo-card__head {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  justify-content: space-between;
}

.gs-promo-card__identity {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  min-width: 0;
}

.gs-promo-card__name {
  margin: 0;
  overflow-wrap: anywhere;
  font-size: 15px;
  font-weight: 650;
  line-height: 1.35;
}

.gs-promo-card__status {
  display: flex;
  flex: none;
  gap: 10px;
  align-items: center;
}

.gs-promo-card__status-label {
  color: var(--gs-muted-foreground);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .04em;
  text-transform: uppercase;
}

.gs-promo-card__facts {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px 18px;
  margin: 0;
  padding: 12px 14px;
  background: var(--gs-surface-subtle);
  border-radius: var(--gs-radius-md);
}

.gs-promo-card__fact {
  min-width: 0;
}

.gs-promo-card__fact dt {
  margin: 0 0 2px;
  color: var(--gs-muted-foreground);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .06em;
  text-transform: uppercase;
}

.gs-promo-card__fact dd {
  margin: 0;
  overflow: hidden;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.gs-promo-card__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-top: auto;
}

.gs-promo-card__actions-spacer {
  flex: 1 1 auto;
}

.gs-promo-card__actions ::v-deep .gs-button--sm {
  gap: 6px;
}
</style>
